import {IssueTracksIcon} from '@primer/octicons-react'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {SubIssueRemovedEventNode} from './issue-viewer-page-query-types'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'
import styles from './SubIssueRemovedEvent.module.css'

export type SubIssueRemovedEventFragment = Pick<
  SubIssueRemovedEventNode,
  'actor' | 'createdAt' | 'databaseId' | 'subIssue'
>

type RollupGroup = SubIssueRemovedEventFragment
type RollupGroups = Record<string, RollupGroup[]>

type SubIssueRemovedEventProps = {
  queryRef: SubIssueRemovedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: RollupGroups
  repositoryId: string
  ownerLogin: string
}

export function SubIssueRemovedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: SubIssueRemovedEventProps) {
  const {actor, createdAt, subIssue, databaseId} = queryRef

  if (!subIssue) {
    return null
  }

  const highlighted = String(subIssue.databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup && rollupGroup['SubIssueRemovedEvent'] ? rollupGroup['SubIssueRemovedEvent'] : []
  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      showAgoTimestamp={false}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueTracksIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.subIssueRemoved[itemsToRender.length === 1 ? 'single' : 'multiple']} `}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <ul className={styles.Box}>
          {itemsToRender.map((item, index) => (
            <SubIssueEventItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${subIssue.databaseId}_${index}`}
              event={item}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />
          ))}
        </ul>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function SubIssueEventItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: SubIssueRemovedEventFragment
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {subIssue} = event

  if (!subIssue) {
    return null
  }

  return (
    <li className={styles.Box_1}>
      <IssueLink data={subIssue} targetRepositoryId={targetRepositoryId} targetOwnerLogin={targetOwnerLogin} />
    </li>
  )
}
